// 绑定状态（docs/04 第 8 节）：通道的来自 smpp-connector 心跳里每条通道的期望与在线 bind 数（决策 D176），
// 客户账号的来自 smpp-server 心跳里每个账号的在线会话（D178），admin 都按实例汇总。
import { formatTime, type Tone } from "@/ui";
import { api } from "./api";
import type { Account, Channel } from "./master";

/** 一条通道在全部出口实例上的 bind 情况；last_error 是最近一次 bind 失败，成功 bind 后不清。 */
export interface ChannelBindRow {
  channel_id: number; want: number; up: number;
  last_error?: string; last_error_at?: string;
}

/** reported_at 为空表示没有新鲜的心跳：connector 没在运行或连不上 Redis。 */
export interface ChannelBindList { reported_at: string | null; items: ChannelBindRow[] }

export const channelBinds = { list: () => api<ChannelBindList>("/channel-binds") };

/** 绑定状态的展示：标签、色调与悬停说明；说明是数组时一项一行。 */
export interface BindStatus { tone: Tone; label: string; detail?: string | string[] }

/** 一条通道的绑定状态；HTTP 通道与停用的通道不连上游，返回 null。list 未加载完时也返回 null，免得整列闪成未上报；
 * 读取失败（failed）时显示读取失败，不能静默成横线、看着像不连上游的通道。 */
export function bindStatus(channel: Channel, list: ChannelBindList | undefined, failed = false): BindStatus | null {
  if (channel.protocol !== "smpp" || channel.status === "disabled") return null;
  if (!list) return failed ? { tone: "neutral", label: "读取失败", detail: "绑定状态读取失败，请刷新后重试。" } : null;
  if (!list.reported_at) return { tone: "neutral", label: "未上报", detail: "最近 30 秒没有 smpp-connector 的心跳：确认 connector 在运行、能连上 Redis。" };
  const row = list.items.find((r) => r.channel_id === channel.id);
  if (!row || row.want === 0) return { tone: "neutral", label: "未分配", detail: "没有出口实例为这条通道保持 bind：确认最大 bind 数大于 0；出口实例清单不为空时确认 connector 在清单里（D157）。" };
  const count = `${row.up}/${row.want}`;
  if (row.up >= row.want) return { tone: "success", label: `已连接 ${count}` };
  const detail = row.last_error ? `最近一次 bind 失败（${formatTime(row.last_error_at ?? "")}）：${row.last_error}` : "正在 bind，还没有失败记录。";
  return row.up === 0 ? { tone: "danger", label: `未连接 ${count}`, detail } : { tone: "warning", label: `部分连接 ${count}`, detail };
}

/** 一条在线的客户会话：所在的 smpp-server 实例、bind 模式、对端地址与 bind 时刻。 */
export interface BoundSession { instance: string; mode: "transmitter" | "receiver" | "transceiver"; remote: string; since: string }

/** 一个客户账号在全部 smpp-server 实例上的在线会话，按 bind 时刻从早到晚；只列有在线会话的账号。 */
export interface AccountBindRow { account_id: number; sessions: BoundSession[] }

/** reported_at 为空表示没有新鲜的心跳：smpp-server 没在运行或连不上 Redis。 */
export interface AccountBindList { reported_at: string | null; items: AccountBindRow[] }

export const accountBinds = { list: () => api<AccountBindList>("/account-binds") };

const BIND_MODE: Record<BoundSession["mode"], string> = { transmitter: "只发", receiver: "只收", transceiver: "收发" };

/** 一个客户账号的绑定状态（D178）；HTTP 账号不 bind，返回 null。没有在线会话的停用账号也返回 null，免得满屏未连接。
 * list 未加载完时返回 null，读取失败时显示读取失败，与通道一致。已连接时悬停逐条列出会话。 */
export function accountBindStatus(account: Account, list: AccountBindList | undefined, failed = false): BindStatus | null {
  if (account.protocol !== "smpp") return null;
  if (!list) return failed ? { tone: "neutral", label: "读取失败", detail: "绑定状态读取失败，请刷新后重试。" } : null;
  if (!list.reported_at) return { tone: "neutral", label: "未上报", detail: "最近 30 秒没有 smpp-server 的心跳：确认 smpp-server 在运行、能连上 Redis。" };
  const sessions = list.items.find((r) => r.account_id === account.id)?.sessions ?? [];
  if (!sessions.length) return account.status === "disabled" ? null : { tone: "neutral", label: "未连接" };
  const detail = sessions.map((s) => `${BIND_MODE[s.mode] ?? s.mode} ${s.remote}，${formatTime(s.since)} 起`);
  return { tone: "success", label: `已连接 ${sessions.length}/${account.max_binds}`, detail };
}
